import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Package, TrendingUp, Bell, Users, LogOut, ChevronRight, Menu, X, Settings, Building2, FileBarChart2, GitCompare, ShieldCheck, Truck, TrendingDown, BellRing, Layers } from 'lucide-react';
import api from '../lib/api';
import Watermark from './Watermark';

const NAV_GROUPS = [
  {
    label: 'Data',
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/parts', icon: Package, label: 'FG Parts' },
      { path: '/materials', icon: TrendingUp, label: 'RM Price Index' },
      { path: '/customers', icon: Building2, label: 'Customers' },
      { path: '/suppliers', icon: Truck, label: 'Suppliers', requiredCap: 'access_suppliers' },
      { path: '/plating', icon: Layers, label: 'Plating', requiredCap: 'access_plating' },
    ],
  },
  {
    label: 'Analysis',
    items: [
      { path: '/reports', icon: FileBarChart2, label: 'Reports' },
      { path: '/revisions', icon: GitCompare, label: 'Rev Compare' },
      { path: '/alerts', icon: Bell, label: 'Alerts', showBadge: true },
      { path: '/margin-alerts', icon: BellRing, label: 'Margin Alerts', allowedRoles: ['admin'] },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/users', icon: Users, label: 'Users', allowedRoles: ['admin'] },
      { path: '/rbac', icon: ShieldCheck, label: 'Roles & Permissions', allowedRoles: ['admin'] },
      { path: '/settings', icon: Settings, label: 'Backup & Restore', allowedRoles: ['admin'] },
    ],
  },
];

const NAV_ITEMS = NAV_GROUPS.flatMap(g => g.items);

function NavItem({ item, isActive, alertCount, collapsed }) {
  const Icon = item.icon;
  const badge = item.showBadge && alertCount > 0;

  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
      className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-sm text-sm font-medium transition-all duration-200 group relative
        ${isActive
          ? 'bg-orange-500/15 text-orange-400 border-l-2 border-orange-500 nav-active'
          : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100 border-l-2 border-transparent'
        }`}
    >
      <Icon size={17} className="flex-shrink-0" />
      {!collapsed && <span className="flex-1">{item.label}</span>}
      {!collapsed && badge && (
        <span className="bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-mono">
          {alertCount > 99 ? '99+' : alertCount}
        </span>
      )}
      {collapsed && badge && (
        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
      )}
    </Link>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await api.get('/alerts/unread-count');
        setAlertCount(res.data.count || 0);
      } catch {}
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const visibleGroups = NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (item.allowedRoles && !item.allowedRoles.includes(user?.role)) return false;
        if (item.requiredCap && user?.role !== 'admin' && !(user?.capabilities || []).includes(item.requiredCap)) return false;
        return true;
      }),
    }))
    .filter(group => group.items.length > 0);

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full bg-slate-900 ${mobile ? 'w-full' : (collapsed ? 'w-16' : 'w-64')} transition-all duration-300`}>
      {/* Logo */}
      <div className={`flex items-center ${collapsed && !mobile ? 'flex-col gap-2 px-2 py-4' : 'gap-3 px-4 py-5'} border-b border-slate-800`}>
        <img src="/anmol-logo.jpg" alt="Anmol" className="w-8 h-8 object-contain rounded-sm flex-shrink-0" />
        {(!collapsed || mobile) && (
          <div className="min-w-0 flex-1">
            <p className="text-white font-heading font-700 text-sm leading-tight truncate">Advanced Anmol</p>
            <p className="text-orange-400 text-xs font-medium truncate">Metcomp Pvt Ltd</p>
          </div>
        )}
        {!mobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`text-slate-500 hover:text-slate-300 transition-colors ${collapsed ? '' : 'ml-auto'}`}
            data-testid="sidebar-toggle"
          >
            <ChevronRight size={16} className={`transition-transform duration-300 ${collapsed ? '' : 'rotate-180'}`} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {visibleGroups.map((group, groupIdx) => (
          <div key={group.label} className={groupIdx > 0 ? 'mt-5' : ''}>
            {(!collapsed || mobile) && (
              <div className="px-5 mb-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                {group.label}
              </div>
            )}
            {collapsed && !mobile && groupIdx > 0 && (
              <div className="mx-3 mb-2 border-t border-slate-700/50" />
            )}
            <div className="space-y-0.5">
              {group.items.map(item => (
                <NavItem
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  alertCount={alertCount}
                  collapsed={collapsed && !mobile}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-800 p-3">
        {(!collapsed || mobile) ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <span className="text-white text-sm font-bold">{user?.name?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-slate-400 text-xs truncate capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-400 transition-colors"
              data-testid="logout-btn"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        ) : (
          <button
            onClick={handleLogout}
            className="w-full flex justify-center text-slate-500 hover:text-red-400 transition-colors p-1"
            data-testid="logout-btn-collapsed"
            title="Logout"
          >
            <LogOut size={15} />
          </button>
        )}
      </div>
    </div>
  );

  // Page title from nav
  const currentNav = NAV_ITEMS.find(n => n.path === location.pathname)
    || (location.pathname === '/margin-compare' ? { label: 'Margin Compare', icon: TrendingDown } : null);
  const pageTitle = currentNav?.label || 'Dashboard';

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <Watermark />
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 flex-shrink-0">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4 flex-shrink-0">
          <button
            className="md:hidden text-slate-600"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>
          <div>
            <h1 className="text-lg font-heading font-700 text-slate-900 leading-tight">{pageTitle}</h1>
            <p className="text-xs text-slate-500">Advanced Anmol Metcomp Pvt Ltd</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/alerts" className="relative" data-testid="topbar-alerts">
              <Bell size={18} className="text-slate-500 hover:text-slate-800 transition-colors" />
              {alertCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-mono">
                  {alertCount > 9 ? '9+' : alertCount}
                </span>
              )}
            </Link>
            <img src="/anmol-logo.jpg" alt="Anmol" className="w-8 h-8 object-contain rounded-sm" />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 animate-fade-in">
          {children}
        </main>
      </div>
    </div>
  );
}
